import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { createApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { CompanyType } from "@entrilia/shared/api/clientTypes";
import { allCompanyTypes } from "@entrilia/shared/api/clientTypes";
import type { JobProgress } from "@entrilia/shared/api/types";
import AsyncJobProgress from "@entrilia/shared/components/AsyncJobProgress";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useJobProgress from "@entrilia/shared/hooks/useJobProgress";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import type { BcTenantCompanies, BcTenantSelection } from "../../../api/clientsManagementTypes";
import { api } from "../../../api/operationsApi";
import UserContext from "../../../context/UserContext";
import type { ClientCodeFromWebsiteUrlAccessors } from "../../../hooks/useClientCodeFromWebsiteUrl";
import { useClientCodeFromWebsiteUrl } from "../../../hooks/useClientCodeFromWebsiteUrl";
import { useExecutionState } from "../../../hooks/useExecutionState";
import { isFormValid } from "../../../utilities/forms";
import { getVariables } from "../../../variables";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import PageTitle from "../../common/PageTitle";
import type { CopyCompanyForm } from "./copyCompanyForm";
import {
  applySourceClientDefaults,
  getInitialCopyCompanyForm,
  mapCopyCompanyFormToRequest,
  validateCopyCompanyForm,
} from "./copyCompanyForm";

const startCopyCompany = withErrorHandling(api.copyCompany.startCopyCompany);

const clientCodeAccessors: ClientCodeFromWebsiteUrlAccessors<CopyCompanyForm> = {
  getClientCode: (form) => form.targetClientCode,
  getWebsiteUrl: (form) => form.newClientWebsiteUrl,
  // The deferred derivation can land after the operator left new-client mode, where the code is picked from a list.
  setClientCode: (form, targetClientCode) => (form.createNewClient ? { ...form, targetClientCode } : form),
};

// A response loaded for a previous target must never offer its companies, so it is matched against the current tenant.
const isResponseForTenant = (response: BcTenantCompanies, tenant: BcTenantSelection | undefined): boolean =>
  !tenant ||
  (response.tenantId?.toLowerCase() === tenant.tenantId.toLowerCase() &&
    response.environment?.toLowerCase() === tenant.environment.toLowerCase());

const CopyCompanyContent = () => {
  const { permissions } = useDefinedContext(UserContext);
  const [form, setForm] = useState<CopyCompanyForm>(getInitialCopyCompanyForm);
  const { executionState, setExecutionState } = useExecutionState();
  const prefillClientCodeFromUrl = useClientCodeFromWebsiteUrl(form, setForm, clientCodeAccessors);

  const canCreateNewClient = permissions.includes("ManageClients");

  const [organizations, fetchOrganizationsError] = useFetch(api.clientProvisioning.getExistingOrganizations);

  const findCompanyInfo = useCallback(
    (clientCode: string) =>
      organizations?.flatMap((org) => org.managesClients).find((client) => client.clientCode === clientCode),
    [organizations]
  );

  const targetOrganizationClientCode = useMemo(() => {
    if (!form.targetClientCode || !organizations) return "";
    const org = organizations.find(
      (o) =>
        !o.isInternalOrg &&
        (o.clientCode === form.targetClientCode ||
          o.managesClients.some((client) => client.clientCode === form.targetClientCode))
    );
    return org?.clientCode ?? "";
  }, [form.targetClientCode, organizations]);

  // The copy runs against the target client's own BC connection, so only its tenant can hold the company to copy into.
  const targetBcTenant = useMemo<BcTenantSelection | undefined>(() => {
    const targetCompany = findCompanyInfo(form.targetClientCode);
    return targetCompany?.bcTenantId && targetCompany.bcEnvironment
      ? { tenantId: targetCompany.bcTenantId, environment: targetCompany.bcEnvironment }
      : undefined;
  }, [findCompanyInfo, form.targetClientCode]);

  const fetchBcCompanies = useCallback(
    async () =>
      targetOrganizationClientCode && !form.createNewCompany
        ? api.clientProvisioning.getExistingBcCompanies(targetOrganizationClientCode, targetBcTenant)
        : createApiResponse<BcTenantCompanies>({
            tenantId: null,
            environment: null,
            companies: [],
            availableTenants: [],
          }),
    [targetOrganizationClientCode, form.createNewCompany, targetBcTenant]
  );

  const [bcCompaniesResponse, bcCompaniesError, { isFetching: isFetchingBcCompanies }] = useFetch(fetchBcCompanies);

  const targetBcCompanies = useMemo(() => {
    if (!bcCompaniesResponse || !isResponseForTenant(bcCompaniesResponse, targetBcTenant)) return [];
    return bcCompaniesResponse.companies.map((c) => ({
      companyId: c.companyId,
      displayName: `${c.displayName} (${c.name})`,
    }));
  }, [bcCompaniesResponse, targetBcTenant]);

  // A self-managed organization also appears in its own managesClients, so codes must be deduplicated.
  const allClientCodes = useMemo(
    () =>
      distinct(
        organizations?.flatMap((org) => [org.clientCode, ...org.managesClients.map((client) => client.clientCode)]) ??
          []
      ).sort(),
    [organizations]
  );

  // Only fund manager clients can be copied into a new client, so organization codes are excluded in that mode.
  const fundManagerClientCodes = useMemo(
    () => organizations?.flatMap((org) => org.managesClients.map((client) => client.clientCode)).sort() ?? [],
    [organizations]
  );

  // The real FundAdmin that owns the source client; umbrella orgs are excluded so the owner isn't the umbrella.
  const sourceOrganization = useMemo(() => {
    if (!form.sourceClientCode || !organizations) return undefined;
    return organizations.find(
      (org) =>
        !org.isInternalOrg &&
        (org.clientCode === form.sourceClientCode ||
          org.managesClients.some((client) => client.clientCode === form.sourceClientCode))
    );
  }, [form.sourceClientCode, organizations]);

  // Restrict target options to other clients under the same FundAdmin to avoid copying into the wrong client.
  const targetClientCodes = useMemo(
    () =>
      sourceOrganization
        ? distinct([
            sourceOrganization.clientCode,
            ...sourceOrganization.managesClients.map((client) => client.clientCode),
          ])
            .filter((clientCode) => clientCode !== form.sourceClientCode)
            .sort()
        : [],
    [sourceOrganization, form.sourceClientCode]
  );

  const handleJobFinished = useCallback(
    (jobProgress: JobProgress) => {
      if (jobProgress.properties["Cancelled"] === "true") {
        setExecutionState({
          status: "warning",
          text: "Copy was cancelled.",
          details: ["The copied company may be incomplete."],
        });
        return;
      }

      if (jobProgress.isSuccess) {
        const isNewClientJob = jobProgress.properties["CreateNewClient"] === "True";
        const newClientCode = jobProgress.properties["TargetClientCode"];
        setExecutionState({
          status: "success",
          details: [isNewClientJob ? "Client copy completed successfully." : "Company copy completed successfully."],
          links:
            isNewClientJob && newClientCode
              ? [{ label: "Open in Admin Portal", href: `${getVariables().ADMIN_PORTAL_URI}/${newClientCode}/home` }]
              : undefined,
        });
      } else {
        setExecutionState({
          status: "error",
          errorMessage: jobProgress.errorMessage,
        });
      }
    },
    [setExecutionState]
  );

  const { jobProgress, startJobProgressCheck } = useJobProgress(api.copyCompany.getCopyCompanyProgress, {
    onJobFinished: handleJobFinished,
  });

  const isTargetBcTenantUnknown = !targetBcTenant && (bcCompaniesResponse?.availableTenants.length ?? 0) > 1;

  const fetchError = fetchOrganizationsError ?? bcCompaniesError;
  if (fetchError) {
    return <LoadingFailedAlert apiError={fetchError} />;
  }

  if (organizations === undefined) {
    return <InlineLoader />;
  }

  const handleCopyCompany = async () => {
    const validationResults = validateCopyCompanyForm(form);
    setForm({ ...form, validationResults });
    if (!isFormValid(validationResults)) {
      return;
    }

    setExecutionState({ status: "in-progress" });

    const payload = mapCopyCompanyFormToRequest(form);
    const [resp, error] = await startCopyCompany(payload);
    if (error) {
      setExecutionState({ status: "error", error });
      return;
    }

    startJobProgressCheck(resp.operationId);
  };

  const handleSourceClientChange = (value: string | null) => {
    setForm((prev) => {
      const next = {
        ...prev,
        sourceClientCode: value || "",
        targetClientCode: "",
        targetBcCompanyId: "",
      };
      return prev.createNewClient ? applySourceClientDefaults(next, findCompanyInfo(next.sourceClientCode)) : next;
    });
  };

  const handleCreateNewClientChange = (createNewClient: boolean) => {
    setForm((prev) => {
      const sourceCompanyInfo = findCompanyInfo(prev.sourceClientCode);
      return applySourceClientDefaults(
        {
          ...prev,
          createNewClient,
          // An organization can be the source in normal mode but not in new-client mode — drop it on switch.
          sourceClientCode: createNewClient && !sourceCompanyInfo ? "" : prev.sourceClientCode,
          targetClientCode: "",
          targetBcCompanyId: "",
          createNewCompany: createNewClient ? true : prev.createNewCompany,
        },
        createNewClient ? sourceCompanyInfo : undefined
      );
    });
  };

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "24rem" }}>
      <PageTitle title="Copy Company" subtitle="📋 What gets copied" defaultExpanded>
        Copy a Business Central company to another client, or to a brand-new client provisioned as part of the copy
        <br />
        <br />
        <strong>What gets copied:</strong>
        <br />
        • Complete Business Central company data
        <br />
        • Company configuration and settings
        <br />
        • All ledger entries and transactions
        <br />
        <br />
        <strong>Automated process includes:</strong>
        <br />
        {(form.createNewCompany || form.createNewClient) && (
          <>
            ✓ Copy BC company to new name
            <br />
          </>
        )}
        {form.createNewClient ? (
          <>
            ✓ Provision new fund manager client linked to the copied company
            <br />
            ✓ Sync Business Central settings &amp; permissions
            <br />
            ✓ Provision DWH infrastructure &amp; full initial sync
            <br />
            ✓ Enable BC sync for the new client
            <br />
          </>
        ) : (
          <>
            ✓ Update application properties (BC &amp; DWH)
            <br />
            ✓ Sync Business Central settings
            <br />
            ✓ Provision DWH infrastructure
            <br />
            ✓ Full DWH sync (recreate schema &amp; import data)
            <br />
            ✓ Enable BC sync for target client
            <br />
          </>
        )}
        ✓ Clear caches
        <br />
        <br />
        ⏱️ <strong>Duration:</strong> Can take up to 60 minutes for companies with large datasets
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <Autocomplete
          fullWidth
          options={form.createNewClient ? fundManagerClientCodes : allClientCodes}
          value={form.sourceClientCode || null}
          onChange={(_, value) => handleSourceClientChange(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Source Client Code"
              error={form.validationResults.sourceClientCode?.isValid === false}
              helperText={form.validationResults.sourceClientCode?.error}
            />
          )}
        />
        <FieldHelp>Client code containing the Business Central company to copy from</FieldHelp>
      </Stack>
      {canCreateNewClient && (
        <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.createNewClient}
                onChange={(e) => handleCreateNewClientChange(e.target.checked)}
              />
            }
            label="Copy into a new client"
          />
          <FieldHelp>
            Create a brand-new fund manager client under the same organization and copy the company into it.
            <br />
            <br />
            The new client is provisioned automatically with the copied Business Central company.
          </FieldHelp>
        </Stack>
      )}
      {form.createNewClient ? (
        <>
          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <TextField
              fullWidth
              label="New Client Code"
              value={form.targetClientCode}
              onChange={(e) => setForm({ ...form, targetClientCode: e.target.value })}
              error={form.validationResults.targetClientCode?.isValid === false}
              helperText={form.validationResults.targetClientCode?.error}
            />
            <FieldHelp>
              Unique code of the new client.
              <br />
              <br />
              Only lowercase letters and digits, no special characters. Must not match an existing client.
            </FieldHelp>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <TextField
              fullWidth
              label="New Client Title"
              value={form.newClientTitle}
              onChange={(e) => setForm({ ...form, newClientTitle: e.target.value })}
              error={form.validationResults.newClientTitle?.isValid === false}
              helperText={form.validationResults.newClientTitle?.error}
            />
            <FieldHelp>Display name of the new fund manager client.</FieldHelp>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <TextField
              fullWidth
              label="Website URL"
              value={form.newClientWebsiteUrl}
              onChange={(e) => setForm({ ...form, newClientWebsiteUrl: e.target.value })}
              onBlur={prefillClientCodeFromUrl}
              error={form.validationResults.newClientWebsiteUrl?.isValid === false}
              helperText={form.validationResults.newClientWebsiteUrl?.error}
            />
            <FieldHelp>
              Public website URL of the new client. The favicon is used as the client&apos;s icon.
              <br />
              <br />
              Optional.
            </FieldHelp>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <FormControl fullWidth>
              <InputLabel>Company Type</InputLabel>
              <Select
                label="Company Type"
                value={form.newClientCompanyType}
                onChange={(e) => setForm({ ...form, newClientCompanyType: e.target.value as CompanyType })}
              >
                {allCompanyTypes.map((companyType) => (
                  <MenuItem key={companyType} value={companyType}>
                    {companyType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FieldHelp>
              Determines how the new client is treated in reports and operations.
              <br />
              <br />
              Prefilled from the source client.
            </FieldHelp>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <FormControl fullWidth>
              <FormLabel component="legend">Enabled Features</FormLabel>
              <FormGroup>
                <FormControlLabel
                  label="Investor Portal"
                  control={
                    <Checkbox
                      checked={form.enableInvestorPortal}
                      onChange={(_, checked) => setForm({ ...form, enableInvestorPortal: checked })}
                    />
                  }
                />
              </FormGroup>
              <FormGroup>
                <FormControlLabel
                  label="Expense Management"
                  control={
                    <Checkbox
                      checked={form.enableExpenseManagement}
                      onChange={(_, checked) => setForm({ ...form, enableExpenseManagement: checked })}
                    />
                  }
                />
              </FormGroup>
              <FormGroup>
                <FormControlLabel
                  label="Portfolio Monitoring Data Collection"
                  control={
                    <Checkbox
                      checked={form.enablePortfolioMonitoringDataCollection}
                      onChange={(_, checked) => setForm({ ...form, enablePortfolioMonitoringDataCollection: checked })}
                    />
                  }
                />
              </FormGroup>
            </FormControl>
            <FieldHelp>
              Features enabled on the new client.
              <br />
              <br />
              Prefilled from the source client; adjust as needed.
            </FieldHelp>
          </Stack>
        </>
      ) : (
        <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
          <Autocomplete
            fullWidth
            options={targetClientCodes}
            disabled={!form.sourceClientCode}
            value={form.targetClientCode || null}
            onChange={(_, value) => setForm({ ...form, targetClientCode: value || "", targetBcCompanyId: "" })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target Client Code"
                error={form.validationResults.targetClientCode?.isValid === false}
                helperText={
                  form.validationResults.targetClientCode?.error ||
                  (!form.sourceClientCode ? "Select source client code first" : "")
                }
              />
            )}
          />
          <FieldHelp>
            Client code where the copied company will be created. Limited to clients under the same fund administrator
            as the source.
          </FieldHelp>
        </Stack>
      )}
      {!form.createNewClient && (
        <FormControlLabel
          control={
            <Checkbox
              checked={form.createNewCompany}
              onChange={(e) =>
                setForm({
                  ...form,
                  createNewCompany: e.target.checked,
                  targetBcCompanyId: "",
                  targetBcCompanyName: "",
                  targetBcCompanyDisplayName: "",
                })
              }
            />
          }
          label="Create new company"
        />
      )}
      {form.createNewClient || form.createNewCompany ? (
        <>
          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <TextField
              fullWidth
              label="Target BC Company Name"
              value={form.targetBcCompanyName}
              onChange={(e) => setForm({ ...form, targetBcCompanyName: e.target.value })}
              error={form.validationResults.targetBcCompanyName?.isValid === false}
              helperText={form.validationResults.targetBcCompanyName?.error}
            />
            <FieldHelp>
              Internal name for the new company in Business Central (e.g., &quot;COMPANY_ABC&quot;).
              <br />
              <br />
              Used as the technical identifier in BC and DWH schema name.
            </FieldHelp>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <TextField
              fullWidth
              label="Target BC Company Display Name"
              value={form.targetBcCompanyDisplayName}
              onChange={(e) => setForm({ ...form, targetBcCompanyDisplayName: e.target.value })}
              error={form.validationResults.targetBcCompanyDisplayName?.isValid === false}
              helperText={form.validationResults.targetBcCompanyDisplayName?.error}
            />
            <FieldHelp>
              User-friendly name displayed in Business Central UI (e.g., &quot;ABC Corporation&quot;).
              <br />
              <br />
              This is what users see when working in Business Central.
            </FieldHelp>
          </Stack>
        </>
      ) : (
        <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
          <Autocomplete
            fullWidth
            options={targetBcCompanies}
            getOptionLabel={(option) => option.displayName}
            value={targetBcCompanies.find((c) => c.companyId === form.targetBcCompanyId) || null}
            onChange={(_, value) => setForm({ ...form, targetBcCompanyId: value?.companyId || "" })}
            loading={isFetchingBcCompanies}
            disabled={!form.targetClientCode || isFetchingBcCompanies}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Existing BC Company"
                error={form.validationResults.targetBcCompanyId?.isValid === false || !!bcCompaniesError}
                helperText={
                  form.validationResults.targetBcCompanyId?.error ||
                  (bcCompaniesError ? "Failed to load BC companies" : "") ||
                  (!form.targetClientCode ? "Select target client code first" : "") ||
                  (isTargetBcTenantUnknown ? "Could not determine the target client's BC tenant" : "")
                }
              />
            )}
          />
          <FieldHelp>
            Select an existing Business Central company from the target client to copy data into.
            <br />
            <br />
            The BC copy operation will be skipped and only application properties and DWH sync will be performed.
          </FieldHelp>
        </Stack>
      )}
      <Divider flexItem />
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCopyCompany}
          loading={executionState.status === "in-progress"}
        >
          {form.createNewClient ? "Copy to New Client" : "Copy Company"}
        </Button>
        <Button
          variant="text"
          color="secondary"
          onClick={() => setForm(getInitialCopyCompanyForm())}
          disabled={executionState.status === "in-progress"}
        >
          Reset
        </Button>
      </Stack>
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
      {jobProgress && <AsyncJobProgress title="Please wait..." jobProgress={jobProgress} />}
    </Stack>
  );
};

export default CopyCompanyContent;
